import { Component, OnDestroy, OnInit, ElementRef, ViewChild, HostListener } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ChatThread,
  ChatMessage,
  ChatThreadStatus,
  ChatThreadListResponse,
  ChatMessageListResponse
} from 'src/app/core/models/message';
import { ChatService } from 'src/app/core/services/chat.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { CallService } from 'src/app/core/services/call.service';
import { CallSession, CallType, IncomingCallEvent } from 'src/app/core/models/call';
import { ChatContact } from 'src/app/core/services/chat.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('remoteAudio') remoteAudioRef?: ElementRef<HTMLAudioElement>;
  @ViewChild('remoteVideo') remoteVideoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('localVideo') localVideoRef?: ElementRef<HTMLVideoElement>;
  // Chat Mode
  chatMode: 'PATIENT' | 'STAFF' = 'PATIENT';


  emojis: string[] = [
    // 😄 Smileys & Emotions
    '😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚',
    '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '🥱',
    '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🫠', '🤑', '😲', '☹️', '🙁', '😖', '😞',
    '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '😡',
    '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '🥴', '😇', '🥳', '🥸', '😈', '👿', '💀', '☠️', '👻', '👽',
    '🤖', '💩',

    // 👋 Gestures & People
    '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '✋',
    '✍️', '👏', '🙌', '👐', '🤲', '🙏', '💅', '🤳', '💪', '🦵', '🦶', '👂', '👃', '🧠', '🦷', '👀', '👁️', '👅', '👄',
    '🫶', '🤝', '✊', '👊', '🤛', '🤜', '💋',

    // ❤️ Hearts & Love
    '❤️', '🩷', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❤️‍🔥', '❤️‍🩹', '💕', '💞', '💓', '💗', '💖',
    '💘', '💝', '💟', '❣️',

    // 🐶 Animals & Nature
    '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻', '🐼', '🐨', '🐯', '🦁', '🐮', '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🐤',
    '🐣', '🦆', '🦅', '🦉', '🦇', '🐺', '🐗', '🐴', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🐜', '🕷️', '🦂', '🐢',
    '🐍', '🦎', '🦖', '🦕', '🐙', '🦑', '🦀', '🐡', '🐠', '🐟', '🐬', '🐳', '🐋', '🦈', '🐊', '🐅', '🐆', '🦓',
    '🦍', '🦧', '🐘', '🦛', '🦏', '🐪', '🐫', '🦒', '🦘', '🐃', '🐂', '🐄', '🐎', '🐖', '🐏', '🐑', '🦙', '🐐',
    '🦌', '🐕', '🐩', '🐈', '🐓', '🦃', '🦚', '🦜', '🦢', '🕊️', '🐇', '🦝', '🦨', '🦡', '🦫', '🦦', '🐁', '🐀',

    // 🍔 Food & Drink
    '🍏', '🍎', '🍐', '🍊', '🍋', '🍌', '🍉', '🍇', '🍓', '🫐', '🍈', '🍒', '🍑', '🥭', '🍍', '🥥', '🥝',
    '🍅', '🍆', '🥑', '🥦', '🥬', '🥒', '🌶️', '🌽', '🥕', '🧄', '🧅', '🥔', '🍠', '🥐', '🥯', '🍞', '🥖',
    '🥨', '🧀', '🥚', '🍳', '🥞', '🧇', '🥓', '🥩', '🍗', '🍖', '🌭', '🍔', '🍟', '🍕', '🥪', '🌮', '🌯',
    '🥙', '🧆', '🥗', '🍝', '🍜', '🍲', '🍛', '🍣', '🍱', '🥟', '🍤', '🍙', '🍚', '🍘', '🍢', '🍡', '🍧',
    '🍨', '🍦', '🥧', '🧁', '🍰', '🎂', '🍮', '🍭', '🍬', '🍫', '🍿', '🍩', '🍪', '🥛', '🧃', '☕', '🍵',
    '🧉', '🍺', '🍻', '🍷', '🥂', '🥃', '🍸', '🍹', '🧊',

    // 🚗 Travel & Places
    '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🚚', '🚛', '🚜', '🏍️', '🛵', '🛺', '🚲',
    '🛴', '🚏', '🛣️', '🛤️', '⛽', '🚨', '🚥', '🚦', '🚧', '🛑', '🚢', '🛳️', '⛴️', '🚤', '⛵', '🛶', '🚁', '🛩️',
    '✈️', '🛫', '🛬', '🚀', '🪂', '🗺️', '🗽', '🗼', '🏰', '🏯', '🏟️', '🎡', '🎢', '🎠', '🏖️', '🏝️', '🏜️',
    '🌋', '🗻', '🏔️', '🏕️', '🏡', '🏠', '🏢', '🏬', '🏣', '🏤', '🏥', '🏦', '🏪', '🏫', '🏩', '💒',

    // 🎁 Activities & Objects
    '🎃', '🎄', '🎆', '🎇', '✨', '🎈', '🎉', '🎊', '🎋', '🎍', '🎎', '🎏', '🎐', '🎑', '🎀', '🎁',
    '🎂', '🍰', '🧁', '🍭', '🍬', '🍫', '🎪', '🎭', '🎨', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎷', '🎺',
    '🎸', '🎻', '🎮', '🕹️', '🎲', '♟️', '🎯', '🎳', '🎽', '🥇', '🥈', '🥉', '🏆', '🏅', '🎖️',

    // 💡 Objects
    '💡', '🔦', '🕯️', '💻', '🖥️', '🖨️', '🖱️', '🖲️', '💾', '📱', '📲', '☎️', '📞', '📟', '📠',
    '📷', '📸', '🎥', '📺', '📻', '📡', '⌚', '⏰', '⏱️', '⏲️', '🕰️', '🔋', '🔌', '💵', '💴', '💶', '💷',
    '💳', '💎', '⚖️', '🧰', '🔧', '🔨', '🪓', '🔩', '⚙️', '🪛', '🧱', '🪚', '🪜', '⚗️', '🧪', '🧫', '🧬',

    // 🔣 Symbols
    '❤️', '💔', '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💬', '👁️‍🗨️', '🗨️', '🗯️', '💭', '💤',
    '✅', '❌', '❗', '❓', '⚠️', '🚫', '🔞', '🔒', '🔓', '🔏', '🔐', '🔑', '🗝️', '🪙', '💰', '🏧', '💲',
    '♻️', '⚙️', '🔆', '🔅', '🔱', '⚜️', '🔰', '🏁', '🚩', '🎌', '🏳️', '🏴', '🏳️‍🌈', '🏴‍☠️'
  ];

  // ✅ role flags
  isStaffUser = false;
  isPatientUser = false;

  // ✅ patient-side contacts
  staffContacts: ChatContact[] = [];
  contactsLoading = false;
  contactsPage = 1;
  contactsLimit = 50;
  contactsTotal = 0;

  selectedContact: ChatContact | null = null; // patient selected staff
  pendingOpenForContact = false;


  // File 
  selectedAttachment: File | null = null;
  selectedAttachmentName = '';
  selectedAttachmentPreviewUrl: string | null = null;
  selectedAttachmentType: 'image' | 'video' | 'pdf' | 'audio' | 'file' | null = null;

  patients: any[] = [];
  patientLoading = false;

  callSeconds = 0;
  callTimerHandle: any = null;
  isCallInitiator = false;
  private processedOfferSdp = new Set<string>();
  private makingOffer = false;
  private isSettingRemoteAnswerPending = false;

  // Voice Recording 
  isRecordingVoice = false;
  recordingSeconds = 0;
  recordingTimer: any = null;

  private mediaRecorder: MediaRecorder | null = null;
  private recordingStream: MediaStream | null = null;
  private recordedChunks: Blob[] = [];

  isMicMuted = false;
  isCamOff = false;

  // threads
  threads: ChatThread[] = [];
  threadPage = 1;
  threadLimit = 20;
  threadTotal = 0;
  threadTotalPages = 1;

  showPatientSheet = false;
  allPatients: any[] = [];
  allPatientsLoading = false;

  selectedThread: ChatThread | null = null;
  showEmojiPicker = false;

  // messages
  messages: ChatMessage[] = [];
  messagesPage = 1;
  messagesLimit = 30;
  messagesTotalPages = 1;
  loadingMessages = false;

  // message tone
  private messageAudio: HTMLAudioElement | null = null;
  private hasUserInteracted = false;
  private soundUnlocked = false;


  // CALL state
  currentCall: CallSession | null = null;
  incomingCall: IncomingCallEvent | null = null;
  callLoading = false;
  activeCallId: string | null = null;
  private outgoingRingtoneAudio: HTMLAudioElement | null = null;
  private incomingRingtoneAudio: HTMLAudioElement | null = null;

  // WebRTC
  pc: RTCPeerConnection | null = null;
  localStream: MediaStream | null = null;
  remoteStream: MediaStream | null = null;

  isInCallUI = false;

  // UI state
  loadingThreads = false;
  errorMessage: string | null = null;
  muteLoading = false;

  messageText = '';
  isSending = false;

  showStaffSheet = false;
  allStaff: any[] = [];
  allStaffLoading = false;

  // filters
  filterStatus: ChatThreadStatus | 'ALL' = 'OPEN';
  filterUnread: 'all' | 'admin' | 'patient' = 'all';
  searchText = '';

  // typing indicator
  isPartnerTyping = false;
  typingTimeoutHandle: any = null;

  private subs = new Subscription();

  @HostListener('document:click')
  onFirstUserInteraction(): void {
    if (this.hasUserInteracted) return;

    this.hasUserInteracted = true;
    this.unlockAllChatSounds();
  }

  constructor(
    private chatSvc: ChatService,
    private patientSvc: PatientService,
    private callSvc: CallService
  ) { }

  ngOnInit(): void {

    this.initMessageSound();
    this.initOutgoingRingtoneSound();
    this.initIncomingRingtoneSound();

    // ✅ role flags FIRST
    this.isStaffUser = this.chatSvc.isStaff();
    this.isPatientUser = this.chatSvc.isPatient();

    console.log('ROLE =>', this.chatSvc.getCurrentRole());
    console.log('USERID =>', this.chatSvc.getCurrentUserId());

    if (this.isPatientUser) {
      this.filterStatus = 'ALL';
      this.filterUnread = 'all';
    }

    // =========================
    // ✅ DATA LOAD (FINAL FIX)
    // =========================

    if (this.isPatientUser) {

      // 🔥 FIRST load staff → THEN threads
      this.loadStaffContacts(() => {
        this.loadThreads(1);
      });

    } else {

      // STAFF → direct threads
      this.loadThreads(1);
    }

    // =========================
    // SOCKETS (UNCHANGED)
    // =========================

    // Incoming Call
    this.subs.add(
      this.chatSvc.onCallIncoming().subscribe((evt) => {
        if (!evt?.callId) return;

        if (this.incomingCall?.callId &&
          String(this.incomingCall.callId) === String(evt.callId)) {
          return;
        }

        this.chatSvc.callJoin(evt.callId);

        this.stopOutgoingRingtone();
        this.incomingCall = evt;
        this.playIncomingRingtone();
      })
    );

    // Call accepted
    this.subs.add(
      this.chatSvc.onCallAccepted().subscribe(async (e: any) => {

        this.stopOutgoingRingtone();
        this.stopIncomingRingtone();

        if (!this.currentCall) return;

        this.currentCall = {
          ...(this.currentCall as any),
          status: 'ACCEPTED'
        };

        this.startCallTimer();

        if (this.isCallInitiator && this.currentCall?._id && this.pc) {
          await this.createAndSendOffer(this.currentCall._id);
        }
      })
    );

    this.subs.add(this.chatSvc.onCallRejected().subscribe(e => this.onCallRejectedUI(e)));
    this.subs.add(this.chatSvc.onCallEnded().subscribe(e => this.onCallEndedUI(e)));

    // signaling
    this.subs.add(this.chatSvc.onCallOffer().subscribe(e => this.onOffer(e)));
    this.subs.add(this.chatSvc.onCallAnswer().subscribe(e => this.onAnswer(e)));
    this.subs.add(this.chatSvc.onCallIce().subscribe(e => this.onIce(e)));

    // =========================
    // REALTIME MESSAGE
    // =========================
    this.subs.add(
      this.chatSvc.onNewMessage().subscribe(evt => {

        if (!evt?.threadId || !evt?.message) return;

        const isCurrent =
          this.selectedThread &&
          String(evt.threadId) === String(this.selectedThread._id);

        // ✅ ALWAYS refresh thread list
        this.loadThreads(this.threadPage);

        if (!isCurrent) return;

        const exists = this.messages.some(
          m => String(m._id) === String(evt.message._id)
        );

        if (!exists) {
          this.messages = [...this.messages, evt.message];
        }
      })
    );

    // =========================
    // TYPING
    // =========================
    this.subs.add(
      this.chatSvc.onTyping().subscribe(evt => {

        if (!this.selectedThread) return;
        if (!evt || String(evt.threadId) !== String(this.selectedThread._id)) return;

        if (this.isPatientUser) {
          if (!this.selectedContact?._id) return;
          if (String(evt.userId) !== String(this.selectedContact._id)) return;
        }

        this.isPartnerTyping = !!evt.isTyping;

        if (this.typingTimeoutHandle) {
          clearTimeout(this.typingTimeoutHandle);
        }

        if (evt.isTyping) {
          this.typingTimeoutHandle = setTimeout(() => {
            this.isPartnerTyping = false;
          }, 3000);
        }
      })
    );

    // =========================
    // ONLINE / OFFLINE
    // =========================

    this.subs.add(
      this.chatSvc.onUserOnline().subscribe((evt: any) => {

        if (!evt) return;

        // STAFF online → patient side
        if (evt.userId) {

          const uid = String(evt.userId);

          this.staffContacts = this.staffContacts.map((u: any) =>
            String(u._id) === uid ? { ...u, isOnline: true } : u
          );

          if (this.selectedContact && String(this.selectedContact._id) === uid) {
            this.selectedContact = { ...this.selectedContact, isOnline: true };
          }
        }
      })
    );

    this.subs.add(
      this.chatSvc.onUserOffline().subscribe((evt: any) => {

        if (!evt) return;

        if (evt.userId) {

          const uid = String(evt.userId);

          this.staffContacts = this.staffContacts.map((u: any) =>
            String(u._id) === uid ? { ...u, isOnline: false } : u
          );

          if (this.selectedContact && String(this.selectedContact._id) === uid) {
            this.selectedContact = { ...this.selectedContact, isOnline: false };
          }
        }
      })
    );

    // =========================
    // REACTIONS
    // =========================
    this.subs.add(
      this.chatSvc.onMessageReaction().subscribe((evt: any) => {

        if (!evt?.messageId) return;

        const msgId = String(evt.messageId);

        this.messages = this.messages.map((m: any) => {
          const currentId = String(m?._id || m?.id || '');
          return currentId === msgId
            ? { ...m, reactions: evt.reactions || [] }
            : m;
        });
      })
    );
  }


  // Message Sound Tone Helper
  private initMessageSound(): void {
    try {
      this.messageAudio = new Audio('assets/images/message.mp3');
      this.messageAudio.load();
    } catch (err) {
      console.warn('Message sound init failed =>', err);
    }
  }

  private playMessageSound(): void {
    if (!this.hasUserInteracted || !this.soundUnlocked) {
      return;
    }

    if (!this.messageAudio) {
      this.initMessageSound();
    }

    if (!this.messageAudio) return;

    try {
      this.messageAudio.currentTime = 0;
      this.messageAudio.play().catch(err => {
        if (err?.name !== 'NotAllowedError') {
          console.warn('Message sound play error =>', err);
        }
      });
    } catch (err) {
      console.warn('Message sound error =>', err);
    }
  }

  private isIncomingMessageMine(message: any): boolean {
    const myId = String(this.chatSvc.getCurrentUserId() || '');
    const senderId = this.getSenderUserId(message);

    if (myId && senderId) {
      return myId === senderId;
    }

    const senderRole = String(message?.senderRole || '').toUpperCase();

    if (this.isPatientUser) {
      return senderRole === 'PATIENT';
    }

    return senderRole !== 'PATIENT';
  }

  private unlockAllChatSounds(): void {
    if (this.soundUnlocked) return;

    if (!this.messageAudio) {
      this.initMessageSound();
    }

    if (!this.outgoingRingtoneAudio) {
      this.initOutgoingRingtoneSound();
    }

    if (!this.incomingRingtoneAudio) {
      this.initIncomingRingtoneSound();
    }

    const audioToUnlock =
      this.messageAudio ||
      this.outgoingRingtoneAudio ||
      this.incomingRingtoneAudio;

    if (!audioToUnlock) return;

    try {
      audioToUnlock.muted = true;

      const playPromise = audioToUnlock.play();

      if (playPromise && (playPromise as any).then) {
        (playPromise as Promise<void>)
          .then(() => {
            audioToUnlock.pause();
            audioToUnlock.currentTime = 0;
            audioToUnlock.muted = false;

            if (this.messageAudio) {
              this.messageAudio.muted = false;
              this.messageAudio.currentTime = 0;
            }

            if (this.outgoingRingtoneAudio) {
              this.outgoingRingtoneAudio.muted = false;
              this.outgoingRingtoneAudio.currentTime = 0;
            }

            if (this.incomingRingtoneAudio) {
              this.incomingRingtoneAudio.muted = false;
              this.incomingRingtoneAudio.currentTime = 0;
            }

            this.soundUnlocked = true;
            console.log('✅ Chat sounds unlocked');
          })
          .catch(err => {
            console.warn('⚠️ Chat sounds unlock failed =>', err);
          });
      } else {
        audioToUnlock.pause();
        audioToUnlock.currentTime = 0;
        audioToUnlock.muted = false;

        if (this.messageAudio) {
          this.messageAudio.muted = false;
          this.messageAudio.currentTime = 0;
        }

        if (this.outgoingRingtoneAudio) {
          this.outgoingRingtoneAudio.muted = false;
          this.outgoingRingtoneAudio.currentTime = 0;
        }

        if (this.incomingRingtoneAudio) {
          this.incomingRingtoneAudio.muted = false;
          this.incomingRingtoneAudio.currentTime = 0;
        }

        this.soundUnlocked = true;
      }
    } catch (err) {
      console.warn('Chat sounds unlock error =>', err);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.cancelVoiceRecording();
    this.stopRecordingTimer();

    if (this.selectedThread) {
      this.chatSvc.leaveThreadRoom(this.selectedThread._id);
    }

    if (this.selectedAttachmentPreviewUrl) {
      URL.revokeObjectURL(this.selectedAttachmentPreviewUrl);
    }

    try {
      this.incomingRingtoneAudio?.pause();
      if (this.incomingRingtoneAudio) {
        this.incomingRingtoneAudio.currentTime = 0;
      }
    } catch { }
  }

  private getMsgId(m: any): string {
    return String(m?._id || m?.id || '');
  }

  // =========================
  // CONTACTS (PATIENT)
  // =========================
  loadStaffContacts(callback?: () => void): void {

    this.chatSvc.getStaffContacts({})
      .subscribe({
        next: (res: any) => {

          this.staffContacts = res.items || [];

          console.log('STAFF CONTACTS =>', this.staffContacts);

          // ✅ sync selectedContact online status
          if (this.selectedContact) {
            const found = this.staffContacts.find(
              u => String(u._id) === String(this.selectedContact!._id)
            );

            if (found) {
              this.selectedContact = {
                ...this.selectedContact,
                isOnline: found.isOnline
              };
            }
          }

          // ✅ callback AFTER data ready
          if (callback) {
            callback();
          }
        },
        error: (err) => {
          console.error('Failed to load staff contacts', err);
        }
      });
  }

  // =========================
  // STAFF-only patient sheet
  // =========================
  openPatientSheet(): void {
    if (!this.isStaffUser) return; // ✅ patient can't open
    this.showPatientSheet = true;

    if (this.allPatients.length) return;
    this.allPatientsLoading = true;

    this.patientSvc.list({ page: 1, limit: 200, q: '', status: '' }).subscribe({
      next: (res) => {
        this.allPatients = res.data.patients || [];
        this.allPatientsLoading = false;
      },
      error: (err) => {
        console.error('patient list (sheet) failed', err);
        this.allPatientsLoading = false;
      }
    });
  }

  openStaffSheet(): void {

    if (!this.isPatientUser) return;

    this.showStaffSheet = true;

    if (this.allStaff.length) return;

    this.allStaffLoading = true;

    this.chatSvc.getStaffContacts({}).subscribe({
      next: (res: any) => {
        this.allStaff = res.items || [];
        this.allStaffLoading = false;
      },
      error: (err) => {
        console.error('staff list failed', err);
        this.allStaffLoading = false;
      }
    });
  }

  closeStaffSheet(): void {
    this.showStaffSheet = false;
  }
  startChatWithStaff(u: any): void {

  this.closeStaffSheet();

  this.selectContact(u); // already working logic
}

  formatTime(date: string | Date | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getInitials(name?: string | null): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) || '';
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return (first + last).toUpperCase();
  }

  getPatientInitials(t: ChatThread): string {
    const p: any = typeof t.patientId === 'string' ? null : t.patientId;
    // fallback if populate missing
    return this.getInitials(p?.fullName || 'Patient');
  }

  getLastMessageTickClasses(t: ChatThread): string[] {
    const last: any = t.lastMessage;

    if (!last) return [];

    const text = (last.text || '').toString().trim();
    if (!text) return [];

    const base = ['bi', 'me-1', 'msg-tick'];

    // =========================
    // STAFF LIST
    // last message staff ne bheja ho to tick dikhayen
    // =========================
    if (this.isStaffUser) {
      if (last.senderRole === 'PATIENT') {
        return [];
      }

      const patientOnline = this.isPatientOnline(t);

      if (!patientOnline) {
        return [...base, 'bi-check'];
      }

      if ((t.unreadForPatient || 0) > 0) {
        return [...base, 'bi-check2-all'];
      }

      return [...base, 'bi-check2-all', 'msg-tick-read'];
    }

    // =========================
    // PATIENT LIST
    // patient ke apne sent msg par tick dikhayen
    // =========================
    if (last.senderRole !== 'PATIENT') {
      return [];
    }

    const staffOnline = !!this.selectedContact?.isOnline;

    if (!staffOnline) {
      return [...base, 'bi-check'];
    }

    if ((t.unreadForAdmin || 0) > 0) {
      return [...base, 'bi-check2-all'];
    }

    return [...base, 'bi-check2-all', 'msg-tick-read'];
  }

  getLastMessagePreview(t: ChatThread): string {

    const last: any = t.lastMessage;

    if (!last) return 'No messages yet';

    if (last.text && last.text.trim()) {
      return last.text;
    }

    if (last.attachment?.mimeType?.startsWith('image/')) {
      return '📷 Image';
    }

    if (last.attachment?.mimeType?.startsWith('video/')) {
      return '🎬 Video';
    }

    if (last.attachment?.mimeType?.startsWith('audio/')) {
      return '🎤 Voice message';
    }

    if (last.attachment?.mimeType === 'application/pdf') {
      return '📄 PDF';
    }

    if (last.attachment) {
      return '📎 Attachment';
    }

    return 'No messages yet';
  }

  getMessageTickClasses(m: ChatMessage): string[] {
    if (!this.isMine(m) || !this.selectedThread) {
      return [];
    }

    const base = ['bi', 'msg-tick'];
    const t: any = this.selectedThread;

    // =========================
    // STAFF SIDE => partner is PATIENT
    // =========================
    if (this.isStaffUser) {
      const patientOnline = this.isPatientOnline(this.selectedThread);

      // patient offline => single tick
      if (!patientOnline) {
        return [...base, 'bi-check'];
      }

      // patient online => at least double tick
      if ((t.unreadForPatient || 0) > 0) {
        return [...base, 'bi-check2-all'];
      }

      // read => double blue
      return [...base, 'bi-check2-all', 'msg-tick-read'];
    }

    // =========================
    // PATIENT SIDE => partner is selected admin
    // =========================
    const staffOnline = !!this.selectedContact?.isOnline;

    // staff offline => single tick
    if (!staffOnline) {
      return [...base, 'bi-check'];
    }

    // admin online => at least double tick
    if ((t.unreadForAdmin || 0) > 0) {
      return [...base, 'bi-check2-all'];
    }

    // read => double blue
    return [...base, 'bi-check2-all', 'msg-tick-read'];
  }

  closePatientSheet(): void {
    this.showPatientSheet = false;
  }

  onPatientRowClick(event: MouseEvent, p: any): void {
    event.stopPropagation(); // backdrop / parent ko click na mile
    this.startChatFromSheet(p);
  }

  startChatFromSheet(p: any): void {
    this.closePatientSheet();
    this.startChatWithPatient(p);
  }

  searchPatients(): void {

    const q = (this.searchText || '').trim();

    // =========================
    // EMPTY SEARCH
    // =========================
    if (!q) {

      if (this.isPatientUser) {
        this.loadStaffContacts(); // ✅ FIXED
        return;
      }

      if (this.isStaffUser) {

        if (this.isStaffChat()) {
          this.loadStaffContactsList(1);
        } else {
          this.patients = [];
        }
      }

      return;
    }

    // =========================
    // PATIENT searching staff
    // =========================
    if (this.isPatientUser) {
      this.loadStaffContacts(); // ✅ FIXED
      return;
    }

    // =========================
    // STAFF searching staff
    // =========================
    if (this.isStaffUser && this.isStaffChat()) {
      this.loadStaffContactsList(1);
      return;
    }

    // =========================
    // STAFF searching patients
    // =========================
    this.patientLoading = true;

    this.patientSvc.list({ page: 1, limit: 20, q, status: '' }).subscribe({
      next: (res) => {
        this.patients = res.data.patients || [];
        this.patientLoading = false;
      },
      error: (err) => {
        console.error('patient search failed', err);
        this.patientLoading = false;
      }
    });
  }

  selectContact(c: ChatContact): void {

    this.selectedContact = c;
    this.isPartnerTyping = false;

    // STAFF CHAT MODE
    if (this.isStaffChat()) {

      // open or get staff thread
      this.chatSvc.openStaffThread(c._id).subscribe({
        next: (thread: ChatThread) => {

          // agar thread list me nahi hai to add karo
          const exists = this.threads.find(t => t._id === thread._id);

          if (!exists) {
            this.threads = [thread, ...this.threads];
          }

          this.selectThread(thread);

        },
        error: (err) => {
          console.error('openStaffThread failed', err);
        }
      });

      return;
    }

    // PATIENT CHAT (old logic)
    if (this.threads.length) {

      const thread = this.threads[0];

      if (!this.selectedThread || this.selectedThread._id !== thread._id) {
        this.selectThread(thread);
        return;
      }

      this.messages = [...this.messages];

      this.chatSvc.markThreadRead(thread._id).subscribe({
        next: (updated) => {
          this.selectedThread = updated;
          this.threads = this.threads.map(t => t._id === updated._id ? updated : t);
        },
        error: (err) => console.error('markThreadRead failed', err)
      });

      return;
    }

    this.pendingOpenForContact = true;
    this.loadThreads(1);
  }

  startChatWithPatient(p: any): void {
    // check: kya is patient ke liye thread already list me hai?
    const existing = this.threads.find(t => {
      const pt: any = typeof t.patientId === 'string' ? null : t.patientId;
      return pt && pt._id === p._id;
    });

    if (existing) {
      this.selectThread(existing);
      return;
    }

    // nahi hai to naya thread open karo
    this.chatSvc.openThread(p._id).subscribe({
      next: (thread: ChatThread) => {
        // thread list me top pe add kar do
        this.threads = [thread, ...this.threads];
        this.selectThread(thread);
      },
      error: (err) => {
        console.error('openThread failed', err);
      }
    });
  }

  setFilterStatus(v: ChatThreadStatus | 'ALL'): void {
    if (this.filterStatus === v) return;
    this.filterStatus = v;
    this.applyFilters();
  }

  setFilterUnread(v: 'all' | 'admin' | 'patient'): void {
    if (this.filterUnread === v) return;
    this.filterUnread = v;
    this.applyFilters();
  }

  // =========================
  // THREADS
  // =========================

  loadThreads(page: number): void {
    if (page < 1) return;
    this.loadingThreads = true;
    this.errorMessage = null;

    const params: any = {
      page,
      limit: this.threadLimit,
      threadType: this.chatMode
    };

    if (this.isStaffUser) {
      params.status = this.filterStatus === 'ALL' ? undefined : this.filterStatus;
      params.unread = this.filterUnread === 'all' ? undefined : this.filterUnread;
      params.search = (this.searchText || '').trim() ? this.searchText : undefined;
    }

    this.chatSvc.listThreads(params).subscribe({
      next: (data: ChatThreadListResponse) => {
        // console.log("THREADS DATA =>", data.threads); // Debug
        this.threads = data?.threads || [];

        const pg = data?.pagination || {
          currentPage: page,
          total: this.threads.length,
          totalPages: 1
        };

        this.threadPage = pg.currentPage || 1;
        this.threadTotal = pg.total || this.threads.length;
        this.threadTotalPages = pg.totalPages || 1;

        this.loadingThreads = false;
      },
      error: (err) => {
        console.error('Failed to load threads', err);
        this.errorMessage = err?.error?.message || 'Failed to load threads.';
        this.loadingThreads = false;
      }
    });
  }

  loadThreadsNext(): void {
    if (this.threadPage < this.threadTotalPages) {
      this.loadThreads(this.threadPage + 1);
    }
  }

  loadThreadsPrev(): void {
    if (this.threadPage > 1) {
      this.loadThreads(this.threadPage - 1);
    }
  }

  applyFilters(): void {
    this.loadThreads(1);
  }

  selectThread(thread: ChatThread): void {
    if (!thread) return;

    if (this.selectedThread) {
      this.chatSvc.leaveThreadRoom(this.selectedThread._id);
    }

    this.selectedThread = thread;
    this.messages = [];
    this.selectedContact = null; // reset

    this.chatSvc.joinThreadRoom(thread._id);

    // 🔥 messages ke baad contact bind karenge
    this.loadMessages(1, true);

    this.chatSvc.markThreadRead(thread._id).subscribe({
      next: (updated) => {
        this.selectedThread = updated;
        this.threads = this.threads.map(t =>
          t._id === updated._id ? updated : t
        );
      }
    });
  }

  // =========================
  // MESSAGES
  // =========================
  loadMessages(page: number, bindContact = false): void {
    if (!this.selectedThread) return;
    if (page < 1) return;

    this.loadingMessages = true;

    this.chatSvc.listMessages(this.selectedThread._id, {
      page,
      limit: this.messagesLimit
    }).subscribe({
      next: (data: ChatMessageListResponse) => {

        const msgs = (data?.messages || []).map((m: any) => ({
          ...m,
          _id: m._id || m.id,
          senderUserId:
            typeof m.senderUserId === 'object'
              ? (m.senderUserId?._id || m.senderUserId?.id || m.senderUserId)
              : m.senderUserId
        }));

        this.messages = msgs;

        // 🔥🔥🔥 CORE FIX START
        if (bindContact && this.isPatientUser) {

          // STEP 1: admin message se detect karo
          const adminMsg = msgs.find(
            (m: any) => String(m.senderRole || '').toUpperCase() !== 'PATIENT'
          );

          if (adminMsg) {

            const sender: any = adminMsg.senderUserId;

            const adminId =
              typeof sender === 'object'
                ? sender._id
                : sender;

            const contact = this.staffContacts.find(
              c => String(c._id) === String(adminId)
            );

            this.selectedContact = {
              _id: adminId,
              fullName: contact?.fullName || sender?.fullName || 'Staff',
              isOnline: contact?.isOnline ?? false
            };

            console.log('✅ selectedContact (from messages) =>', this.selectedContact);
          }

          // STEP 2: fallback → first staff contact
          if (!this.selectedContact && this.staffContacts.length) {

            const first = this.staffContacts[0];

            this.selectedContact = {
              _id: first._id,
              fullName: first.fullName,
              isOnline: first.isOnline
            };

            console.log('⚠️ fallback selectedContact =>', this.selectedContact);
          }
        }
        // 🔥🔥🔥 CORE FIX END

        this.loadingMessages = false;
      },
      error: (err) => {
        console.error('Failed to load messages', err);
        this.loadingMessages = false;
      }
    });
  }

  loadMessagesNext(): void {
    if (this.messagesPage < this.messagesTotalPages) {
      this.loadMessages(this.messagesPage + 1);
    }
  }

  loadMessagesPrev(): void {
    if (this.messagesPage > 1) {
      this.loadMessages(this.messagesPage - 1);
    }
  }

  // =========================
  // SEND + TYPING
  // =========================

  onMessageInputChange(): void {
    if (!this.selectedThread) return;
    this.chatSvc.emitTyping(this.selectedThread._id, true);
  }

  sendMessage(): void {
    if (!this.selectedThread) return;

    const text = (this.messageText || '').trim();
    const file = this.selectedAttachment;

    if (!text && !file) return;

    this.isSending = true;

    let targetUserId: string | null = null;

    if (this.isPatientUser) {

      // ✅ FIRST: selectedContact
      if (this.selectedContact?._id) {
        targetUserId = this.selectedContact._id;
      }

      // 🔥 FALLBACK: messages
      if (!targetUserId && this.messages.length) {

        const adminMsg = this.messages.find(
          (m: any) => String(m.senderRole || '').toUpperCase() !== 'PATIENT'
        );

        if (adminMsg) {
          const sender: any = adminMsg.senderUserId;

          targetUserId =
            typeof sender === 'object'
              ? sender._id
              : sender;
        }
      }
    }

    console.log('🔥 FINAL targetUserId =>', targetUserId);

    this.chatSvc.sendMessage(
      this.selectedThread._id,
      text,
      targetUserId,
      file
    ).subscribe({
      next: (res) => {

        this.selectedThread = { ...(res.thread as any) };

        this.threads = this.threads.map(t =>
          t._id === res.thread._id ? res.thread : t
        );

        this.messageText = '';
        this.removeSelectedAttachment();
        this.showEmojiPicker = false;
        this.isSending = false;

        this.chatSvc.emitTyping(this.selectedThread!._id, false);
      },
      error: (err) => {
        console.error('sendMessage failed', err);
        this.isSending = false;
      }
    });
  }

  // =========================
  // THREAD TITLE logic updated
  // =========================
  getThreadTitle(t: ChatThread): string {

    const currentUserId = String(this.chatSvc.getCurrentUserId());

    // =========================
    // 🟢 PATIENT SIDE
    // =========================
    if (this.isPatientUser) {

      // 1️⃣ try participants
      const other = (t.participants || []).find(
        (u: any) => String(u?._id || u) !== currentUserId
      );

      if (other && typeof other === 'object') {
        return other.fullName;
      }

      // 2️⃣ fallback → selectedContact
      if (this.selectedContact?.fullName) {
        return this.selectedContact.fullName;
      }

      // 3️⃣ fallback → last message sender
      const last: any = t.lastMessage;

      if (last?.senderUserId) {

        // 🔥 try match from staffContacts
        const senderId =
          typeof last.senderUserId === 'object'
            ? last.senderUserId._id
            : last.senderUserId;

        const contact = this.staffContacts.find(
          c => String(c._id) === String(senderId)
        );

        if (contact?.fullName) {
          return contact.fullName;
        }

        // 🔥 DIRECT backend name use karo
        if (typeof last.senderUserId === 'object' && last.senderUserId.fullName) {
          return last.senderUserId.fullName;
        }

        if (contact?.fullName) {
          return contact.fullName;
        }

        // 🔥 fallback → selectedContact
        if (this.selectedContact?.fullName) {
          return this.selectedContact.fullName;
        }
      }

      return 'Unknown';
    }

    // =========================
    // 🔵 STAFF SIDE
    // =========================
    // 🔵 STAFF CHAT MODE
    if (this.isStaffChat()) {

      const other = (t.participants || []).find(
        (u: any) => String(u?._id || u) !== currentUserId
      );

      if (other && typeof other === 'object') {
        return other.fullName;
      }

      if (typeof other === 'string') {
        const contact = this.staffContacts?.find(
          c => String(c._id) === String(other)
        );
        if (contact?.fullName) {
          return contact.fullName;
        }
      }

      return 'Staff';
    }

    // 🔵 STAFF → PATIENT CHAT
    const p: any = typeof t.patientId === 'object' ? t.patientId : null;
    return p?.fullName || 'Patient';
  }

  getThreadInitials(t: ChatThread): string {

    const currentUserId = String(this.chatSvc.getCurrentUserId());

    const other = (t.participants || []).find(
      (u: any) => String(u?._id || u) !== currentUserId
    );

    if (other && typeof other === 'object') {
      return this.getInitials(other.fullName);
    }

    return '??';
  }

  getThreadSubtitle(t: ChatThread): string {

    if (t.threadType === 'STAFF') {

      const currentUser = this.chatSvc.getCurrentUserId();

      const other = (t.participants || []).find(
        (u: any) => String(u?._id || u) !== String(currentUser)
      );

      if (typeof other === 'object') {
        return other.role || 'Staff';
      }

      const contact = this.staffContacts?.find(
        c => String(c._id) === String(other)
      );

      return contact?.role || 'Staff';
    }

    const p: any = typeof t.patientId === 'string' ? null : t.patientId;

    return `${p?.phone || ''}`;
  }



  getPatientUnreadBadgeForUser(contact: ChatContact): number {
    // abhi per-user unread backend me nahi hai
    // is liye sirf selected user par shared unread dikha do
    if (!this.selectedContact || String(this.selectedContact._id) !== String(contact._id)) {
      return 0;
    }

    const t = this.threads?.length ? this.threads[0] : null;
    return Number(t?.unreadForPatient || 0);
  }

  getPatientLastMessageTickClassesForUser(contact: ChatContact): string[] {

    const t: any = this.threads?.length ? this.threads[0] : null;
    const last: any = t?.lastMessage;

    if (!last) return [];
    if (!last.text || !String(last.text).trim()) return [];

    if (!this.isMine(last)) return [];

    const base = ['bi', 'me-1', 'msg-tick'];

    if (!contact?.isOnline) {
      return [...base, 'bi-check'];
    }

    if ((t?.unreadForPatient || 0) > 0) {
      return [...base, 'bi-check2-all'];
    }

    return [...base, 'bi-check2-all', 'msg-tick-read'];
  }

  // ============================
  // Patient Chat panel seprate
  // ============================
  private normalizeUserId(value: any): string {
    if (!value) return '';

    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value._id) return String(value._id);
    if (typeof value === 'object' && value.id) return String(value.id);

    return String(value);
  }

  // presence dot logic
  isPatientOnline(t: ChatThread): boolean {
    const p: any = typeof t.patientId === 'object' ? t.patientId : null;
    return !!p?.isOnline;
  }

  isStaffOnline(): boolean {
    return !!this.selectedContact?.isOnline;
  }

  getHeaderPresenceOnline(): boolean {

    // STAFF SIDE
    if (this.isStaffUser) {
      return this.isPatientOnline(this.selectedThread!);
    }

    // PATIENT SIDE

    // ✅ primary
    if (this.selectedContact?.isOnline !== undefined) {
      return !!this.selectedContact?.isOnline;
    }

    // ✅ fallback
    const currentUserId = String(this.chatSvc.getCurrentUserId());

    const other = (this.selectedThread?.participants || []).find(
      (u: any) => String(u?._id || u) !== currentUserId
    );

    if (other && typeof other === 'object') {
      const user = other as { isOnline?: boolean };
      return !!user.isOnline;
    }

    return false;
  }

  // 👇 YE NAYA HELPER ADD KARO
  getMessageSenderLabel(m: ChatMessage): string {
    // agar ye mera (admin) message hai
    if (this.isMine(m)) {
      return 'You';
    }

    // warna patient ka naam show karo
    const p: any =
      this.selectedThread && typeof this.selectedThread.patientId === 'string'
        ? null
        : this.selectedThread?.patientId;

    return p?.fullName || 'Patient';
  }

  private getSenderUserId(msg: any): string {
    const raw = msg?.senderUserId;

    if (!raw) return '';

    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw._id) return String(raw._id);
    if (typeof raw === 'object' && raw.id) return String(raw.id);

    return String(raw);
  }

  getThreadBadge(t: ChatThread): string {
    if (t.unreadForAdmin > 0) {
      return `${t.unreadForAdmin} new`;
    }
    return '';
  }

  getThreadStatusClass(t: ChatThread): string {
    switch (t.status) {
      case 'CLOSED':
        return 'badge bg-secondary';
      case 'ARCHIVED':
        return 'badge bg-dark';
      default:
        return 'badge bg-success';
    }
  }

  formatDate(date: string | Date | null | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString();
  }

  isMine(msg: ChatMessage): boolean {
    const myId = this.chatSvc.getCurrentUserId();
    const senderId = this.getSenderUserId(msg);

    if (myId && senderId) {
      return String(senderId) === String(myId);
    }

    // fallback only if sender id missing
    if (this.isPatientUser) {
      return msg.senderRole === 'PATIENT';
    }

    return msg.senderRole !== 'PATIENT';
  }

  messageBubbleClass(msg: ChatMessage): string {
    return this.isMine(msg) ? 'msg-bubble me' : 'msg-bubble them';
  }

  messageTextClass(msg: ChatMessage): string {
    return this.isMine(msg) ? 'text-white' : 'text-dark';
  }

  toggleEmojiPicker(): void {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  appendEmoji(emoji: string): void {
    this.messageText = (this.messageText || '') + emoji;
    this.onMessageInputChange();        // typing event bhi chalay gaa
  }

  onMicClick(): void {
    if (this.isRecordingVoice) {
      this.stopVoiceRecording();
      return;
    }

    this.startVoiceRecording();
  }

  isThreadMuted(t: ChatThread | null | undefined): boolean {
    if (!t) return false;

    const idsRaw: any[] = (t as any).mutedForUserIds || [];
    const ids = idsRaw.map(x => String(x));

    // 🔥 get current userId (jo bhi available ho)
    const currentUserId =
      (this.chatSvc as any).currentUserId ||
      (localStorage.getItem('userId')) ||
      (JSON.parse(localStorage.getItem('user') || 'null')?._id);

    // ✅ if still not found, fallback (at least icon change ho)
    if (!currentUserId) {
      return ids.length > 0; // single-admin setup me fine
    }

    return ids.includes(String(currentUserId));
  }

  toggleThreadMute(): void {
    if (!this.selectedThread) return;

    const id = this.selectedThread._id;
    this.muteLoading = true;

    this.chatSvc.toggleMute(id).subscribe({
      next: (updated) => {
        console.log('MUTE UPDATED THREAD =>', updated); // 👈 check mutedForUserIds here

        // ✅ Always replace reference (Angular detect changes easily)
        this.selectedThread = { ...(updated as any) } as any;

        this.threads = this.threads.map(t =>
          t._id === updated._id ? ({ ...(updated as any) } as any) : t
        );

        this.muteLoading = false;
      },
      error: (err) => {
        console.error('toggleMute failed', err);
        this.muteLoading = false;
      }
    });
  }

  //======================
  // React Chat Message
  // =====================
  react(m: any, reaction: string): void {
    const messageId = String(m?._id || m?.id || '');
    if (!messageId) return;

    console.log('REACTION CLICKED =>', reaction);

    this.chatSvc.reactToMessage(messageId, reaction).subscribe({
      next: (res: any) => {
        console.log('REACTION API RESPONSE =>', res);

        const updated = res?.data || res;
        const updatedId = String(updated?._id || updated?.id || messageId);

        this.messages = this.messages.map((x: any) => {
          const xid = String(x?._id || x?.id || '');
          return xid === updatedId
            ? { ...x, reactions: updated?.reactions || [] }
            : x;
        });
      },
      error: (err) => {
        console.error('reactToMessage failed', err);
        console.error('reactToMessage failed payload =>', err?.error);
      }
    });
  }

  //====================
  //  File Upload
  // ===================
  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || !files.length) return;

    const file = files[0];

    this.selectedAttachment = file;
    this.selectedAttachmentName = file.name;

    // old preview cleanup
    if (this.selectedAttachmentPreviewUrl) {
      URL.revokeObjectURL(this.selectedAttachmentPreviewUrl);
      this.selectedAttachmentPreviewUrl = null;
    }

    if (file.type.startsWith('image/')) {
      this.selectedAttachmentType = 'image';
      this.selectedAttachmentPreviewUrl = URL.createObjectURL(file);
    } else if (file.type.startsWith('video/')) {
      this.selectedAttachmentType = 'video';
      this.selectedAttachmentPreviewUrl = URL.createObjectURL(file);
    } else if (file.type.startsWith('audio/')) {
      this.selectedAttachmentType = 'audio';
      this.selectedAttachmentPreviewUrl = URL.createObjectURL(file);
    } else if (file.type === 'application/pdf') {
      this.selectedAttachmentType = 'pdf';
      this.selectedAttachmentPreviewUrl = null;
    } else {
      this.selectedAttachmentType = 'audio';
      this.selectedAttachmentPreviewUrl = null;
    }

    input.value = '';
  }

  removeSelectedAttachment(): void {
    if (this.selectedAttachmentPreviewUrl) {
      URL.revokeObjectURL(this.selectedAttachmentPreviewUrl);
    }

    this.selectedAttachment = null;
    this.selectedAttachmentName = '';
    this.selectedAttachmentPreviewUrl = null;
    this.selectedAttachmentType = null;
  }

  isImageAttachment(m: any): boolean {
    return !!m?.attachment?.mimeType?.startsWith('image/');
  }

  isVideoAttachment(m: any): boolean {
    return !!m?.attachment?.mimeType?.startsWith('video/');
  }

  isPdfAttachment(m: any): boolean {
    return m?.attachment?.mimeType === 'application/pdf';
  }

  hasAttachment(m: any): boolean {
    return !!m?.attachment?.url;
  }

  getAttachmentName(m: any): string {
    return m?.attachment?.fileName || 'Attachment';
  }

  //=================================
  //  Calling TS
  // ================================

  onAudioCall(): void { this.startOutgoingCall('AUDIO'); }
  onVideoCall(): void { this.startOutgoingCall('VIDEO'); }

  private bindRemoteMedia(): void {
    try {
      if (this.remoteAudioRef?.nativeElement && this.remoteStream) {
        this.remoteAudioRef.nativeElement.srcObject = this.remoteStream;
        this.remoteAudioRef.nativeElement.autoplay = true;
        this.remoteAudioRef.nativeElement.muted = false;
        this.remoteAudioRef.nativeElement.volume = 1;

        this.remoteAudioRef.nativeElement.play().then(() => {
          console.log('remote audio playing...');
        }).catch(err => {
          console.warn('remote audio play blocked =>', err);
        });
      }

      if (this.remoteVideoRef?.nativeElement && this.remoteStream) {
        this.remoteVideoRef.nativeElement.srcObject = this.remoteStream;
        this.remoteVideoRef.nativeElement.autoplay = true;
        this.remoteVideoRef.nativeElement.playsInline = true;
        this.remoteVideoRef.nativeElement.muted = false;

        this.remoteVideoRef.nativeElement.play().catch(err => {
          console.warn('remote video play blocked =>', err);
        });
      }
    } catch (err) {
      console.error('bindRemoteMedia error =>', err);
    }
  }

  private bindLocalMedia(): void {
    try {
      if (this.localVideoRef?.nativeElement && this.localStream) {
        this.localVideoRef.nativeElement.srcObject = this.localStream;
        this.localVideoRef.nativeElement.muted = true;
        this.localVideoRef.nativeElement.play().catch(err => {
          console.warn('local video play blocked =>', err);
        });
      }
    } catch (err) {
      console.error('bindLocalMedia error =>', err);
    }
  }

  toggleMuteMic(): void {
    this.isMicMuted = !this.isMicMuted;
    const track = this.localStream?.getAudioTracks?.()[0];
    if (track) track.enabled = !this.isMicMuted;
  }

  toggleCamera(): void {
    this.isCamOff = !this.isCamOff;
    const track = this.localStream?.getVideoTracks?.()[0];
    if (track) track.enabled = !this.isCamOff;
  }

  // =========================
  // Outgoing Call fix (STAFF vs PATIENT)
  // =========================
  startOutgoingCall(type: CallType): void {
    if (!this.selectedThread) return;

    this.isCallInitiator = true;

    // STAFF => call patient
    if (this.isStaffUser) {
      const patient: any =
        typeof this.selectedThread.patientId === 'string'
          ? { _id: this.selectedThread.patientId }
          : this.selectedThread.patientId;

      this.callSvc.startCall({
        type,
        receiverPatientId: patient._id,
        threadId: this.selectedThread._id
      }).subscribe({
        next: async (session) => {
          console.log('OUTGOING CALL SESSION =>', session);

          this.currentCall = session;
          this.currentCall.status = 'RINGING' as any;

          this.chatSvc.callJoin(session._id);
          this.isInCallUI = true;
          this.playOutgoingRingtone();

          // ✅ prepare only
          await this.initPeerConnection();
          await this.startLocalMedia(type);

          console.log('Caller ready, waiting for call:accepted before sending offer...');
        },
        error: (err) => console.error('startOutgoingCall error =>', err)
      });

      return;
    }

    // PATIENT => call selected staff contact
    if (this.isPatientUser) {
      if (!this.selectedContact?._id) return;

      this.callSvc.startCall({
        type,
        receiverUserId: this.selectedContact._id,
        threadId: this.selectedThread._id
      }).subscribe({
        next: async (session) => {
          console.log('OUTGOING CALL SESSION =>', session);

          this.currentCall = session;
          this.currentCall.status = 'RINGING' as any;

          this.chatSvc.callJoin(session._id);
          this.isInCallUI = true;
          this.playOutgoingRingtone();

          // ✅ prepare only
          await this.initPeerConnection();
          await this.startLocalMedia(type);

          console.log('Caller ready, waiting for call:accepted before sending offer...');
        },
        error: (err) => console.error('startOutgoingCall error =>', err)
      });
    }
  }

  acceptIncomingCall(): void {
    if (!this.incomingCall) return;
    this.stopIncomingRingtone();

    this.isCallInitiator = false;

    const callId = this.incomingCall.callId;
    const incomingType = this.incomingCall.type;

    console.log('ACCEPT INCOMING CALL =>', this.incomingCall);

    this.incomingCall = null;

    this.currentCall = {
      _id: callId,
      type: incomingType,
      status: 'ACCEPTED'
    } as any;

    this.chatSvc.callJoin(callId);
    this.chatSvc.callAccept(callId);

    this.isInCallUI = true;

    this.initPeerConnection()
      .then(async () => {
        try {
          await this.startLocalMedia(incomingType);
        } catch (err: any) {
          console.warn('Video media failed on accept, falling back to audio =>', err);

          // VIDEO fail ho to audio-only fallback
          if (incomingType === 'VIDEO') {
            await this.startLocalMedia('AUDIO');
          } else {
            throw err;
          }
        }

        console.log('receiver local media ready, waiting for offer...');
      })
      .catch(err => {
        console.error('acceptIncomingCall error =>', err);
      });
  }

  rejectIncomingCall(): void {
    const callId = this.incomingCall?.callId || this.currentCall?._id;
    if (!callId) {
      this.cleanupCall();
      return;
    }

    this.stopIncomingRingtone();

    this.chatSvc.callReject(callId);

    this.callSvc.endCall(callId, 'REJECTED').subscribe({
      next: () => { },
      error: () => { }
    });

    this.cleanupCall();
  }

  async initPeerConnection(): Promise<void> {
    if (this.pc) return;

    console.log('initPeerConnection start');

    this.pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' }
      ]
    });

    this.remoteStream = new MediaStream();

    this.pc.ontrack = (event) => {
      console.log('REMOTE TRACK RECEIVED =>', event);

      event.streams[0].getTracks().forEach(track => {
        const alreadyExists = this.remoteStream!
          .getTracks()
          .some(t => t.id === track.id);

        if (!alreadyExists) {
          this.remoteStream!.addTrack(track);
        }
      });

      console.log('remoteStream tracks =>', this.remoteStream?.getTracks());
      this.bindRemoteMedia();
    };

    this.pc.onicecandidate = (event) => {
      if (!event.candidate || !this.currentCall) return;

      console.log('ICE CANDIDATE =>', event.candidate);
      this.chatSvc.callIce(this.currentCall._id, event.candidate);
    };

    this.pc.onconnectionstatechange = () => {
      console.log('PC connectionState =>', this.pc?.connectionState);
    };

    this.pc.oniceconnectionstatechange = () => {
      console.log('PC iceConnectionState =>', this.pc?.iceConnectionState);
    };

    this.pc.onsignalingstatechange = () => {
      console.log('PC signalingState =>', this.pc?.signalingState);
    };
  }

  async startLocalMedia(type: CallType): Promise<void> {
    try {
      // agar stream already hai to dobara mat mango
      if (this.localStream && this.localStream.getTracks().length) {
        console.log('Local media already exists, skipping getUserMedia');
        return;
      }

      const constraints: MediaStreamConstraints =
        type === 'VIDEO'
          ? { audio: true, video: true }
          : { audio: true, video: false };

      console.log('REQUESTING USER MEDIA =>', constraints);

      this.localStream = await navigator.mediaDevices.getUserMedia(constraints);

      console.log('LOCAL STREAM =>', this.localStream);
      console.log('LOCAL AUDIO TRACKS =>', this.localStream.getAudioTracks());
      console.log('LOCAL VIDEO TRACKS =>', this.localStream.getVideoTracks());

      const senders = this.pc?.getSenders?.() || [];

      this.localStream.getTracks().forEach(track => {
        const alreadySending = senders.some(s => s.track && s.track.kind === track.kind);

        if (!alreadySending) {
          console.log('ADDING LOCAL TRACK =>', track.kind, track.id);
          this.pc!.addTrack(track, this.localStream!);
        } else {
          console.log('TRACK ALREADY EXISTS, skipping =>', track.kind, track.id);
        }
      });

      this.bindLocalMedia();
    } catch (err: any) {
      console.error('startLocalMedia error =>', err);
      throw err;
    }
  }

  async createAndSendOffer(callId: string): Promise<void> {
    if (!this.pc) return;

    const offer = await this.pc.createOffer();
    console.log('CREATED OFFER =>', offer);

    await this.pc.setLocalDescription(offer);
    console.log('LOCAL DESCRIPTION SET =>', this.pc.localDescription);

    this.chatSvc.callOffer(callId, offer);
  }

  // socket handlers
  async onOffer(payload: any): Promise<void> {
    const { callId, sdp } = payload;
    console.log('ON OFFER =>', payload);

    if (!sdp) return;

    if (!this.currentCall) {
      this.currentCall = { _id: callId } as any;
    }

    this.chatSvc.callJoin(callId);
    await this.initPeerConnection();

    const offerKey = sdp?.sdp || JSON.stringify(sdp);

    // same offer dobara aaye to ignore
    if (this.processedOfferSdp.has(offerKey)) {
      console.warn('Duplicate offer ignored');
      return;
    }

    // agar already stable state me remoteDescription set ho chuki hai
    // to same/dusri duplicate offer ko ignore karo
    if (
      this.pc!.signalingState === 'stable' &&
      this.pc!.remoteDescription &&
      this.pc!.currentRemoteDescription
    ) {
      console.warn('Offer ignored because peer is already stable with remote description set');
      return;
    }

    try {
      // agar local media abhi tak ready nahi hui to try karo
      if (!this.localStream) {
        try {
          await this.startLocalMedia((this.currentCall?.type as CallType) || 'AUDIO');
        } catch (err: any) {
          console.warn('startLocalMedia failed inside onOffer, trying audio fallback =>', err);

          if ((this.currentCall?.type as CallType) === 'VIDEO') {
            await this.startLocalMedia('AUDIO');
          }
        }
      }

      await this.pc!.setRemoteDescription(new RTCSessionDescription(sdp));
      console.log('REMOTE DESCRIPTION SET (offer)');

      const answer = await this.pc!.createAnswer();
      console.log('CREATED ANSWER =>', answer);

      // important guard
      if (this.pc!.signalingState !== 'have-remote-offer') {
        console.warn('Skipping setLocalDescription(answer), signalingState is =>', this.pc!.signalingState);
        return;
      }

      await this.pc!.setLocalDescription(answer);
      console.log('LOCAL DESCRIPTION SET (answer)');

      this.processedOfferSdp.add(offerKey);
      this.chatSvc.callAnswer(callId, answer);
    } catch (err) {
      console.error('onOffer error =>', err);
    }
  }

  async onAnswer(payload: any): Promise<void> {
    const { sdp } = payload;
    console.log('ON ANSWER =>', payload);

    if (!this.pc || !sdp) return;

    try {
      // caller side par answer sirf tab set honi chahiye
      // jab local offer pehle se set ho
      if (this.pc.signalingState !== 'have-local-offer') {
        console.warn('Ignoring answer because signalingState is =>', this.pc.signalingState);
        return;
      }

      await this.pc.setRemoteDescription(new RTCSessionDescription(sdp));
      console.log('REMOTE DESCRIPTION SET (answer)');
    } catch (err) {
      console.error('onAnswer error =>', err);
    }
  }

  async onIce(payload: any): Promise<void> {
    const { candidate } = payload;
    console.log('ON ICE =>', payload);

    if (!this.pc || !candidate) return;
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
      console.log('ICE CANDIDATE ADDED');
    } catch (e) {
      console.error('addIceCandidate error', e);
    }
  }

  endCall(): void {
    const callId = this.currentCall?._id || this.incomingCall?.callId;

    if (!callId) {
      this.cleanupCall();
      return;
    }

    const isRingingState =
      !!this.incomingCall ||
      String((this.currentCall as any)?.status || '').toUpperCase() === 'RINGING';

    // Agar abhi call accept nahi hui thi, to isy reject/cancel treat karo
    if (isRingingState) {
      this.chatSvc.callReject(callId);

      this.callSvc.endCall(callId, 'REJECTED').subscribe({
        next: () => { },
        error: () => { }
      });

      this.cleanupCall();
      return;
    }

    // Connected / accepted call
    this.chatSvc.callEnd(callId, 'HANGUP');

    this.callSvc.endCall(callId, 'HANGUP').subscribe({
      next: () => { },
      error: () => { }
    });

    this.cleanupCall();
  }

  cleanupCall(): void {
    this.stopOutgoingRingtone();
    this.stopIncomingRingtone();
    this.stopCallTimer();
    this.callSeconds = 0;
    this.isMicMuted = false;
    this.isCamOff = false;
    this.isCallInitiator = false;

    // force UI close on both ringing and connected cases
    this.incomingCall = null;
    this.isInCallUI = false;

    this.processedOfferSdp.clear();
    this.makingOffer = false;
    this.isSettingRemoteAnswerPending = false;

    try {
      this.localStream?.getTracks().forEach(t => {
        try { t.stop(); } catch { }
      });
    } catch { }

    try {
      this.remoteStream?.getTracks().forEach(t => {
        try { t.stop(); } catch { }
      });
    } catch { }

    this.localStream = null;
    this.remoteStream = null;

    try {
      this.pc?.close();
    } catch { }

    this.pc = null;

    if (this.localVideoRef?.nativeElement) {
      this.localVideoRef.nativeElement.srcObject = null;
    }

    if (this.remoteVideoRef?.nativeElement) {
      this.remoteVideoRef.nativeElement.srcObject = null;
    }

    if (this.remoteAudioRef?.nativeElement) {
      this.remoteAudioRef.nativeElement.srcObject = null;
    }

    const leaveCallId = this.currentCall?._id || this.incomingCall?.callId;
    if (leaveCallId) {
      this.chatSvc.callLeave(leaveCallId);
    }

    this.currentCall = null;
    this.isInCallUI = false;
    this.incomingCall = null;
  }

  onCallRejectedUI(e: any) {
    console.log('CALL REJECTED EVENT =>', e);

    this.stopOutgoingRingtone();
    this.stopIncomingRingtone();
    this.cleanupCall();
  }

  onCallEndedUI(e: any) {
    console.log('CALL ENDED EVENT =>', e);

    this.stopOutgoingRingtone();
    this.stopIncomingRingtone();
    this.cleanupCall();
  }

  formatCallTime(total: number): string {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  startCallTimer(): void {
    this.stopCallTimer();
    this.callSeconds = 0;
    this.callTimerHandle = setInterval(() => this.callSeconds++, 1000);
  }

  stopCallTimer(): void {
    if (this.callTimerHandle) clearInterval(this.callTimerHandle);
    this.callTimerHandle = null;
  }

  // incoming modal name fix
  getIncomingCallerText(): string {
    if (!this.incomingCall) return '';
    return (
      (this.incomingCall as any).displayText ||
      (this.incomingCall.fromName ? `${this.incomingCall.fromName} is calling you` : 'Incoming call')
    );
  }



  // Voice Message
  private getSupportedAudioMimeType(): string {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/ogg'
    ];

    for (const t of types) {
      if ((window as any).MediaRecorder?.isTypeSupported?.(t)) {
        return t;
      }
    }

    return '';
  }

  private startRecordingTimer(): void {
    this.stopRecordingTimer();
    this.recordingSeconds = 0;
    this.recordingTimer = setInterval(() => {
      this.recordingSeconds++;
    }, 1000);
  }

  private stopRecordingTimer(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
  }

  formatRecordingTime(total: number): string {
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  async startVoiceRecording(): Promise<void> {
    try {
      if (this.isSending) return;
      if (this.selectedAttachment) return; // pehle se koi file selected hai

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.recordingStream = stream;
      this.recordedChunks = [];

      const mimeType = this.getSupportedAudioMimeType();

      this.mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        try {
          const finalMimeType =
            this.mediaRecorder?.mimeType ||
            mimeType ||
            'audio/webm';

          const blob = new Blob(this.recordedChunks, { type: finalMimeType });

          const extension = finalMimeType.includes('ogg')
            ? 'ogg'
            : finalMimeType.includes('mp4')
              ? 'm4a'
              : finalMimeType.includes('mpeg')
                ? 'mp3'
                : 'webm';

          const fileName = `voice-note-${Date.now()}.${extension}`;
          const file = new File([blob], fileName, { type: finalMimeType });

          if (this.selectedAttachmentPreviewUrl) {
            URL.revokeObjectURL(this.selectedAttachmentPreviewUrl);
          }

          this.selectedAttachment = file;
          this.selectedAttachmentName = file.name;
          this.selectedAttachmentType = 'audio';
          this.selectedAttachmentPreviewUrl = URL.createObjectURL(blob);

          console.log('VOICE FILE =>', {
            name: file.name,
            type: file.type,
            size: file.size
          });
        } finally {
          this.cleanupVoiceRecorder();
        }
      };

      this.mediaRecorder.start(250);
      this.isRecordingVoice = true;
      this.startRecordingTimer();

      console.log('Voice recording started...');
    } catch (err) {
      console.error('startVoiceRecording failed =>', err);
      this.cleanupVoiceRecorder();
    }
  }

  stopVoiceRecording(): void {
    try {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        this.cleanupVoiceRecorder();
        return;
      }

      this.mediaRecorder.stop();
      console.log('Voice recording stopped...');
    } catch (err) {
      console.error('stopVoiceRecording failed =>', err);
      this.cleanupVoiceRecorder();
    }
  }

  cancelVoiceRecording(): void {
    try {
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.onstop = null;
        this.mediaRecorder.stop();
      }
    } catch { }

    this.cleanupVoiceRecorder();
    this.recordedChunks = [];
  }

  private cleanupVoiceRecorder(): void {
    this.isRecordingVoice = false;
    this.stopRecordingTimer();

    try {
      this.recordingStream?.getTracks().forEach(t => {
        try { t.stop(); } catch { }
      });
    } catch { }

    this.recordingStream = null;
    this.mediaRecorder = null;
  }

  isAudioAttachment(m: any): boolean {
    return !!m?.attachment?.mimeType?.startsWith('audio/');
  }

  // Call Sound tone Code Here::::
  private initOutgoingRingtoneSound(): void {
    try {
      this.outgoingRingtoneAudio = new Audio('assets/images/calling.mp3');
      this.outgoingRingtoneAudio.loop = true;
      this.outgoingRingtoneAudio.load();
    } catch (err) {
      console.warn('Outgoing ringtone init failed =>', err);
    }
  }

  private playOutgoingRingtone(): void {
    if (!this.hasUserInteracted || !this.soundUnlocked) {
      return;
    }

    if (!this.outgoingRingtoneAudio) {
      this.initOutgoingRingtoneSound();
    }

    if (!this.outgoingRingtoneAudio) return;

    try {
      this.outgoingRingtoneAudio.currentTime = 0;
      this.outgoingRingtoneAudio.play().catch(err => {
        if (err?.name !== 'NotAllowedError') {
          console.warn('Outgoing ringtone play error =>', err);
        }
      });
    } catch (err) {
      console.warn('Outgoing ringtone error =>', err);
    }
  }

  private stopOutgoingRingtone(): void {
    try {
      this.outgoingRingtoneAudio?.pause();
      if (this.outgoingRingtoneAudio) {
        this.outgoingRingtoneAudio.currentTime = 0;
      }
    } catch (err) {
      console.warn('Outgoing ringtone stop error =>', err);
    }
  }


  // Incoming Call Sound
  private initIncomingRingtoneSound(): void {
    try {
      this.incomingRingtoneAudio = new Audio('assets/images/icoming.mp3');
      this.incomingRingtoneAudio.loop = true;
      this.incomingRingtoneAudio.load();
    } catch (err) {
      console.warn('Incoming ringtone init failed =>', err);
    }
  }

  private playIncomingRingtone(): void {
    if (!this.hasUserInteracted || !this.soundUnlocked) {
      return;
    }

    if (this.incomingRingtoneAudio) {
      this.incomingRingtoneAudio.muted = false;
      this.incomingRingtoneAudio.currentTime = 0;
    }

    if (!this.incomingRingtoneAudio) return;

    try {
      this.incomingRingtoneAudio.currentTime = 0;
      this.incomingRingtoneAudio.play().catch(err => {
        if (err?.name !== 'NotAllowedError') {
          console.warn('Incoming ringtone play error =>', err);
        }
      });
    } catch (err) {
      console.warn('Incoming ringtone error =>', err);
    }
  }

  private stopIncomingRingtone(): void {
    try {
      this.incomingRingtoneAudio?.pause();
      if (this.incomingRingtoneAudio) {
        this.incomingRingtoneAudio.currentTime = 0;
      }
    } catch (err) {
      console.warn('Incoming ringtone stop error =>', err);
    }
  }

  //////////////////////////////////////
  // CHat Switch 
  /////////////////////////////////////
  toggleChatMode(): void {
    if (!this.isStaffUser) return;

    // leave old room
    if (this.selectedThread) {
      this.chatSvc.leaveThreadRoom(this.selectedThread._id);
    }

    this.chatMode = this.chatMode === 'PATIENT' ? 'STAFF' : 'PATIENT';

    // reset EVERYTHING
    this.selectedThread = null;
    this.selectedContact = null;
    this.messages = [];
    this.threads = [];

    // reload properly
    this.loadThreads(1);

    if (this.chatMode === 'STAFF') {
      this.loadStaffContactsList(1);
    } else {
      this.loadStaffContacts();
    }
  }

  isPatientChat(): boolean {
    return this.chatMode === 'PATIENT';
  }

  isStaffChat(): boolean {
    return this.chatMode === 'STAFF';
  }

  loadStaffContactsList(page: number): void {

    this.contactsLoading = true;

    const query = {
      page,
      limit: this.contactsLimit,
      q: (this.searchText || '').trim()
    };

    const request$ = this.chatMode === 'STAFF'
      ? this.chatSvc.getStaffContacts(query)
      : this.chatSvc.getContacts(query);

    request$.subscribe({

      next: (res: any) => {

        this.staffContacts = res.items || [];

        const pg = res.pagination || {};

        this.contactsPage = pg.currentPage || page;
        this.contactsTotal = pg.total || this.staffContacts.length;

        this.contactsLoading = false;

      },

      error: (err) => {

        console.error('contacts failed', err);
        this.contactsLoading = false;

      }

    });

  }
}
